import { HugeiconsIcon } from "@hugeicons/react";
import { dirOf, useLang } from "../i18n/lang";
import {
  Alert02Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowDown01Icon,
  Attachment01Icon,
  Calendar03Icon,
  Cancel01Icon,
  Certificate01Icon,
  Coins01Icon,
  Copy01Icon,
  Delete02Icon,
  DiamondIcon,
  DocumentAttachmentIcon,
  Download04Icon,
  FilterHorizontalIcon,
  GoldIcon,
  GoldIngotsIcon,
  Grid2X2Icon,
  ListViewIcon,
  Image01Icon,
  ImageNotFound01Icon,
  InformationCircleIcon,
  LinkSquare02Icon,
  Loading03Icon,
  NecklaceIcon,
  Note01Icon,
  PencilEdit02Icon,
  PlusSignIcon,
  QrCodeIcon,
  Refresh01Icon,
  Route01Icon,
  RulerIcon,
  Search01Icon,
  Share08Icon,
  Stamp01Icon,
  StarIcon,
  Store01Icon,
  Tag01Icon,
  Tick02Icon,
  Undo02Icon,
  User03Icon,
  Video01Icon,
  WeightScaleIcon,
  ZoomInAreaIcon,
  ZoomOutAreaIcon,
} from "@hugeicons/core-free-icons";

/**
 * Every glyph in this app comes from here, so "is that a Hugeicon?" is a
 * question with one place to check. Names are affordances, not icon names.
 */
export const icons = {
  pieces: NecklaceIcon,
  prices: GoldIngotsIcon,
  add: PlusSignIcon,
  search: Search01Icon,
  filter: FilterHorizontalIcon,
  refresh: Refresh01Icon,
  viewGrid: Grid2X2Icon,
  viewList: ListViewIcon,
  edit: PencilEdit02Icon,
  delete: Delete02Icon,
  close: Cancel01Icon,
  check: Tick02Icon,
  undo: Undo02Icon,
  chevronDown: ArrowDown01Icon,
  /** Written for RTL; `Icon` mirrors these two when the document is LTR. */
  forward: ArrowLeft01Icon,
  back: ArrowRight01Icon,
  metal: GoldIcon,
  weight: WeightScaleIcon,
  stone: DiamondIcon,
  hallmark: Stamp01Icon,
  maker: User03Icon,
  size: RulerIcon,
  year: Calendar03Icon,
  ownership: Tag01Icon,
  valuation: Coins01Icon,
  condition: Note01Icon,
  provenance: Route01Icon,
  authenticity: Certificate01Icon,
  image: Image01Icon,
  /** Marks the one media file a record is represented by. */
  thumbnail: StarIcon,
  noImage: ImageNotFound01Icon,
  video: Video01Icon,
  file: DocumentAttachmentIcon,
  attach: Attachment01Icon,
  info: InformationCircleIcon,
  alert: Alert02Icon,
  spinner: Loading03Icon,
  qr: QrCodeIcon,
  copy: Copy01Icon,
  share: Share08Icon,
  openLink: LinkSquare02Icon,
  shop: Store01Icon,
  download: Download04Icon,
  zoomIn: ZoomInAreaIcon,
  zoomOut: ZoomOutAreaIcon,
} as const;

export type IconName = keyof typeof icons;

/**
 * An arrow means "onward", not "leftward". These two are the only glyphs in
 * the set that have to mirror when the writing direction flips.
 */
const MIRRORED: Partial<Record<IconName, IconName>> = {
  forward: "back",
  back: "forward",
};

/** 16 beside text · 20 chrome · 24 bottom tabs — the sizes DESIGN.md fixes. */
export type IconSize = 16 | 20 | 24 | 32 | 48;

interface IconProps {
  name: IconName;
  size?: IconSize;
  className?: string;
  /** Set only on an icon that is the sole content of a control. */
  label?: string;
}

export function Icon({ name, size = 20, className, label }: IconProps) {
  const rtl = dirOf(useLang()) === "rtl";
  const resolved = rtl ? name : (MIRRORED[name] ?? name);
  return (
    <HugeiconsIcon
      icon={icons[resolved]}
      size={size}
      color="currentColor"
      strokeWidth={1.75}
      className={className}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
    />
  );
}
